import { Suspense } from 'react'

import type { LandingAction, LandingBlock, LandingPromoRuntime } from '@entities/LandingPage'

import { landingBlockComponents } from '@features/landing-page-builder/model/blockRegistry'
import { reportUnknownLandingBlock } from '@features/landing-page-builder/model/reportUnknownBlock'
import { useAddressStore } from '@features/shipping-methods'

interface LandingBlockMapperProps {
  blocks?: LandingBlock[];
  actions?: LandingAction[] | null;
  promo?: LandingPromoRuntime | null;
}

/*
  Renders the blocks of a landing page in order. Blocks arrive already filtered
  by state and sorted (see prepareLandingBlocks); unknown types are skipped so a
  backend that ships a new block type never breaks an existing page.
*/
export function LandingBlockMapper ({ blocks, actions, promo }: LandingBlockMapperProps) {
  const { shopId } = useAddressStore()

  return (
    <>
      {blocks?.map((block, index) => {
        const BlockComponent = block.type ? landingBlockComponents[block.type] : null

        if (!BlockComponent) {
          // Skip silently for the visitor, but make it visible to us: an unknown
          // type means the backend ships a block this release cannot render.
          reportUnknownLandingBlock(block)
          return null
        }

        return (
          <Suspense key={block.id ?? `${block.type}-${index}`}>
            <BlockComponent
              block={block}
              payload={block.payload}
              shopId={shopId}
              actions={actions}
              promo={promo}
            />
          </Suspense>
        )
      })}
    </>
  )
}
